import * as i0 from '@angular/core';
import { InjectionToken, signal, inject, computed, effect, untracked, Directive, input, numberAttribute, booleanAttribute, Component, ChangeDetectionStrategy } from '@angular/core';
import { BehaviorSubject, Subject, switchMap, timer, EMPTY, tap, of, delay, finalize, takeUntil } from 'rxjs';
import { outputFromObservable } from '@angular/core/rxjs-interop';
function NgProgressbar_Conditional_3_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "div", 3);
  }
}
function NgProgressbar_Conditional_4_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 4);
    i0.ɵɵelement(1, "div", 5);
    i0.ɵɵelementEnd();
  }
}
const defaultOptions = {
  min: 8,
  max: 100,
  speed: 200,
  debounceTime: 0,
  trickleSpeed: 300,
  relative: false,
  flat: false,
  spinner: false,
  direction: 'ltr+',
  spinnerPosition: 'right',
  trickleFunc: n => {
    if (n >= 0 && n < 20) {
      return 10;
    }
    if (n >= 20 && n < 50) {
      return 4;
    }
    if (n >= 50 && n < 80) {
      return 2;
    }
    if (n >= 80 && n < 99) {
      return 0.5;
    }
    return 0;
  }
};
const NG_PROGRESS_OPTIONS = new InjectionToken('NG_PROGRESS_OPTIONS', {
  providedIn: 'root',
  factory: () => defaultOptions
});
function provideNgProgressOptions(options) {
  return {
    provide: NG_PROGRESS_OPTIONS,
    useValue: {
      ...defaultOptions,
      ...options
    }
  };
}
var TriggerType = /*#__PURE__*/function (TriggerType) {
  TriggerType["START"] = "START";
  TriggerType["COMPLETE"] = "COMPLETE";
  return TriggerType;
}(TriggerType || {});
let NgProgressRef = /*#__PURE__*/(() => {
  class NgProgressRef {
    constructor() {
      this._config = signal(inject(NG_PROGRESS_OPTIONS));
      this._progress = signal(0);
      this._active = signal(false);
      this.active = computed(() => this._active());
      this.progress = computed(() => this._progress());
      this.config = computed(() => this._config());
      this._trigger = new BehaviorSubject(null);
      // Progress start source event (used to cancel onComplete delays)
      this._started = new Subject();
      this.started = this._started.asObservable();
      // Progress ended source event
      this._completed = new Subject();
      this.completed = this._completed.asObservable();
      let sub$;
      effect(onCleanup => {
        const config = this.config();
        untracked(() => {
          sub$?.unsubscribe();
          sub$ = this._trigger.pipe(switchMap(trigger => {
            if (trigger === TriggerType.START) {
              return timer(config.debounceTime).pipe(switchMap(() => this.onTrickling(config)));
            } else if (trigger === TriggerType.COMPLETE) {
              return this.onComplete(config);
            }
            return EMPTY;
          })).subscribe();
        });
        onCleanup(() => sub$?.unsubscribe());
      });
    }
    /**
     * Start the progress
     */
    start() {
      this._started.next();
      this._trigger.next(TriggerType.START);
      this._active.set(true);
    }
    /**
     * Complete the progress
     */
    complete() {
      this._trigger.next(TriggerType.COMPLETE);
    }
    /**
     * Increment the progress
     */
    inc(amount) {
      const n = this.progress();
      if (!this.active()) {
        this.start();
      } else {
        if (typeof amount !== 'number') {
          amount = this.config().trickleFunc(n);
        }
        this.set(n + amount);
      }
    }
    /**
     * Set the progress
     */
    set(n) {
      this._active.set(true);
      this._progress.set(this.clamp(n));
    }
    /**
     * Set config
     */
    setConfig(config) {
      this._config.set({
        ...this.config(),
        ...config
      });
    }
    /**
     * Clamps a value to be between min and max
     */
    clamp(n) {
      return Math.max(this.config().min, Math.min(this.config().max, n));
    }
    /**
     * Keeps incrementing the progress
     */
    onTrickling(config) {
      if (!this.active()) {
        this.set(config.min);
      }
      return timer(0, config.trickleSpeed).pipe(tap(() => this.inc()));
    }
    /**
     * Completes then resets the progress
     */
    onComplete(config) {
      if (!this.active()) {
        return EMPTY;
      }
      // Emit completed
      this._completed.next();
      return of({}).pipe(
      // Complete the progress
      tap(() => this._progress.set(100)),
      // Deactivate the progress after a tiny delay
      delay(config.speed * 1.7), tap(() => this._active.set(false)),
      // Use a tiny delay before resetting
      delay(config.speed),
      // Force the progress to reset even it got cancelled
      finalize(() => this._progress.set(0)),
      // Cancel any of the finalizing delays if the progress has started again
      takeUntil(this._started));
    }
    static {
      this.ɵfac = function NgProgressRef_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NgProgressRef)();
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: NgProgressRef,
        selectors: [["", "ngProgressRef", ""]],
        exportAs: ["ngProgressRef"],
        standalone: true
      });
    }
  }
  return NgProgressRef;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let NgProgressbar = /*#__PURE__*/(() => {
  class NgProgressbar {
    constructor() {
      this.config = inject(NG_PROGRESS_OPTIONS);
      /** Progress bar worker */
      this.progressRef = inject(NgProgressRef, {
        host: true,
        self: true
      });
      /** Initializes inputs from the global config */
      this.min = input(this.config.min, {
        transform: numberAttribute
      });
      this.max = input(this.config.max, {
        transform: numberAttribute
      });
      this.speed = input(this.config.speed, {
        transform: numberAttribute
      });
      this.trickleSpeed = input(this.config.trickleSpeed, {
        transform: numberAttribute
      });
      this.debounceTime = input(this.config.debounceTime, {
        transform: numberAttribute
      });
      this.flat = input(this.config.flat, {
        transform: booleanAttribute
      });
      this.spinner = input(this.config.spinner, {
        transform: booleanAttribute
      });
      this.relative = input(this.config.relative, {
        transform: booleanAttribute
      });
      this.trickleFunc = input(this.config.trickleFunc);
      this.spinnerPosition = input(this.config.spinnerPosition);
      this.direction = input(this.config.direction);
      this.progressTransform = computed(() => {
        return `translate3d(${this.progressRef.progress()}%,0,0)`;
      });
      this.started = outputFromObservable(this.progressRef.started);
      this.completed = outputFromObservable(this.progressRef.completed);
      effect(() => {
        const config = {
          max: this.max() > 0 && this.max() <= 100 ? this.max() : 100,
          min: this.min() < 100 && this.min() >= 0 ? this.min() : 0,
          speed: this.speed(),
          trickleSpeed: this.trickleSpeed(),
          trickleFunc: this.trickleFunc(),
          debounceTime: this.debounceTime()
        };
        // Update progress bar config when inputs change
        untracked(() => this.progressRef.setConfig(config));
      });
    }
    start() {
      this.progressRef.start();
    }
    complete() {
      this.progressRef.complete();
    }
    inc(n) {
      this.progressRef.inc(n);
    }
    set(n) {
      this.progressRef.set(n);
    }
    static {
      this.ɵfac = function NgProgressbar_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NgProgressbar)();
      };
    }
    static {
      this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
        type: NgProgressbar,
        selectors: [["ng-progress"]],
        hostAttrs: ["role", "progressbar"],
        hostVars: 10,
        hostBindings: function NgProgressbar_HostBindings(rf, ctx) {
          if (rf & 2) {
            i0.ɵɵattribute("spinnerPosition", ctx.spinnerPosition())("direction", ctx.direction());
            i0.ɵɵstyleProp("--_ng-progress-speed", ctx.speed() + "ms");
            i0.ɵɵclassProp("ng-progress-bar", true)("ng-progress-bar-active", ctx.progressRef.active())("ng-progress-bar-relative", ctx.relative());
          }
        },
        inputs: {
          min: [1, "min"],
          max: [1, "max"],
          speed: [1, "speed"],
          trickleSpeed: [1, "trickleSpeed"],
          debounceTime: [1, "debounceTime"],
          flat: [1, "flat"],
          spinner: [1, "spinner"],
          relative: [1, "relative"],
          trickleFunc: [1, "trickleFunc"],
          spinnerPosition: [1, "spinnerPosition"],
          direction: [1, "direction"]
        },
        outputs: {
          started: "started",
          completed: "completed"
        },
        exportAs: ["ngProgress"],
        standalone: true,
        features: [i0.ɵɵProvidersFeature([NgProgressRef]), i0.ɵɵStandaloneFeature],
        decls: 5,
        vars: 4,
        consts: [[1, "ng-progress-bar-wrapper"], [1, "ng-bar-placeholder"], [1, "ng-bar"], [1, "ng-meteor"], [1, "ng-spinner"], [1, "ng-spinner-icon"]],
        template: function NgProgressbar_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵelementStart(0, "div", 0)(1, "div", 1)(2, "div", 2);
            i0.ɵɵtemplate(3, NgProgressbar_Conditional_3_Template, 1, 0, "div", 3);
            i0.ɵɵelementEnd()();
            i0.ɵɵtemplate(4, NgProgressbar_Conditional_4_Template, 2, 0, "div", 4);
            i0.ɵɵelementEnd();
          }
          if (rf & 2) {
            i0.ɵɵadvance(2);
            i0.ɵɵstyleProp("transform", ctx.progressTransform());
            i0.ɵɵadvance();
            i0.ɵɵconditional(!ctx.flat() ? 3 : -1);
            i0.ɵɵadvance();
            i0.ɵɵconditional(ctx.spinner() ? 4 : -1);
          }
        },
        styles: ["[_nghost-%COMP%]{--_ng-progress-thickness: var(--ng-progress-thickness, 2);--_ng-progress-thickness-px: calc(var(--_ng-progress-thickness) * 1px);--_ng-progress-thickness-add-one: calc(var(--_ng-progress-thickness) + 1);--_ng-progress-meteor-position-px: calc(var(--_ng-progress-thickness-add-one) * -1px);--_ng-progress-color: var(--ng-progress-color, #1B95E0);--_ng-progress-holder-color: var(--ng-progress-holder-color, transparent);--_ng-progress-ease: var(--ng-progress-ease, linear);--_ng-progress-spinner-thickness: var(--ng-progress-spinner-thickness, 2);--_ng-progress-spinner-thickness-px: calc(var(--_ng-progress-spinner-thickness) * 1px);--_ng-progress-spinner-spacing: var(--ng-progress-spinner-spacing, 15);--_ng-progress-spinner-spacing-px: calc(var(--_ng-progress-spinner-spacing) * 1px);--_ng-progress-spinner-size: var(--ng-progress-spinner-size, 18);--_ng-progress-spinner-size-px: calc(var(--_ng-progress-spinner-size) * 1px);z-index:999999;pointer-events:none}.ng-progress-bar-active[_nghost-%COMP%]   .ng-progress-bar-wrapper[_ngcontent-%COMP%]{filter:alpha(opacity=100);opacity:1;transition:none}.ng-progress-bar-active[_nghost-%COMP%]   .ng-bar[_ngcontent-%COMP%]{transition:all var(--_ng-progress-speed) var(--_ng-progress-ease)}.ng-progress-bar-relative[_nghost-%COMP%]   .ng-progress-bar-wrapper[_ngcontent-%COMP%]{position:relative}[direction=\"ltr+\"][_nghost-%COMP%]   .ng-meteor[_ngcontent-%COMP%], [direction=ltr-][_nghost-%COMP%]   .ng-meteor[_ngcontent-%COMP%]{rotate:calc(var(--_ng-progress-thickness-add-one) * 1deg)}[direction=\"ltr+\"][_nghost-%COMP%]   .ng-bar[_ngcontent-%COMP%], [direction=\"rtl+\"][_nghost-%COMP%]   .ng-bar[_ngcontent-%COMP%]{margin-left:-100%}[direction=\"ltr+\"][_nghost-%COMP%]   .ng-meteor[_ngcontent-%COMP%], [direction=\"rtl+\"][_nghost-%COMP%]   .ng-meteor[_ngcontent-%COMP%]{right:0}[direction=\"ltr+\"][_nghost-%COMP%]   .ng-meteor[_ngcontent-%COMP%], [direction=rtl-][_nghost-%COMP%]   .ng-meteor[_ngcontent-%COMP%]{top:var(--_ng-progress-meteor-position-px)}[direction=ltr-][_nghost-%COMP%]   .ng-meteor[_ngcontent-%COMP%], [direction=\"rtl+\"][_nghost-%COMP%]   .ng-meteor[_ngcontent-%COMP%]{bottom:var(--_ng-progress-meteor-position-px)}[direction=ltr-][_nghost-%COMP%]   .ng-bar-placeholder[_ngcontent-%COMP%], [direction=\"rtl+\"][_nghost-%COMP%]   .ng-bar-placeholder[_ngcontent-%COMP%]{transform:rotate(180deg)}[direction=ltr-][_nghost-%COMP%]   .ng-spinner-icon[_ngcontent-%COMP%], [direction=\"rtl+\"][_nghost-%COMP%]   .ng-spinner-icon[_ngcontent-%COMP%]{animation-direction:reverse}[direction=\"rtl+\"][_nghost-%COMP%]   .ng-meteor[_ngcontent-%COMP%], [direction=rtl-][_nghost-%COMP%]   .ng-meteor[_ngcontent-%COMP%]{rotate:calc(var(--_ng-progress-thickness-add-one) * -1deg)}[_nghost-%COMP%]   .ng-spinner[_ngcontent-%COMP%]{top:var(--_ng-progress-spinner-spacing-px)}[spinnerPosition=left][_nghost-%COMP%]   .ng-spinner[_ngcontent-%COMP%]{left:var(--_ng-progress-spinner-spacing-px)}[spinnerPosition=right][_nghost-%COMP%]   .ng-spinner[_ngcontent-%COMP%]{right:var(--_ng-progress-spinner-spacing-px)}.ng-progress-bar-wrapper[_ngcontent-%COMP%]{position:fixed;z-index:999999;top:0;left:0;width:100%;transform:scale(1);filter:alpha(opacity=0);opacity:0;transition:opacity var(--_ng-progress-speed) var(--_ng-progress-ease)}.ng-bar-placeholder[_ngcontent-%COMP%]{position:absolute;height:var(--_ng-progress-thickness-px);width:100%}.ng-bar[_ngcontent-%COMP%]{width:100%;height:100%;transform:translate(-100%,0,0);background:var(--_ng-progress-color);transition:all var(--_ng-progress-speed) var(--_ng-progress-ease)}.ng-meteor[_ngcontent-%COMP%]{display:block;position:absolute;width:100px;height:100%;opacity:1;box-shadow:0 0 10px var(--_ng-progress-color),0 0 5px var(--_ng-progress-color)}.ng-spinner[_ngcontent-%COMP%]{position:absolute;display:block;z-index:1031;top:10px}.ng-spinner-icon[_ngcontent-%COMP%]{box-sizing:border-box;animation:_ngcontent-%COMP%_spinner-animation .25s linear infinite;border-style:solid;border-color:transparent;border-radius:50%;border-top-color:var(--_ng-progress-color);border-left-color:var(--_ng-progress-color);width:var(--_ng-progress-spinner-size-px);height:var(--_ng-progress-spinner-size-px);border-width:var(--_ng-progress-spinner-thickness-px)}@keyframes _ngcontent-%COMP%_spinner-animation{0%{transform:rotate(0)}to{transform:rotate(360deg)}}"],
        changeDetection: 0
      });
    }
  }
  return NgProgressbar;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * Public API Surface of ngx-progressbar
 */

/**
 * Generated bundle index. Do not edit.
 */

export { NG_PROGRESS_OPTIONS, NgProgressRef, NgProgressbar, provideNgProgressOptions };
